import { useMemo } from 'react';
import { format } from 'date-fns';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

const SITES = {
  specialist_dental_london: { label: 'SDL', dot: '#C8A86B' },
  shihab_romeed:            { label: 'SR',  dot: '#44403C' },
  london_dental_academy:    { label: 'LDA', dot: '#3B82F6' },
};

const STATUS_DOT = {
  confirmed: 'bg-emerald-500',
  pending:   'bg-amber-500',
  completed: 'bg-blue-500',
  cancelled: 'bg-red-400',
  no_show:   'bg-red-600',
};

export function CalendarEvent({ event }) {
  const data = event.data || {};
  const site = SITES[data.siteKey] || { dot: '#a8a29e' };
  const statusClass = STATUS_DOT[data.status] || 'bg-stone-400';

  const serviceName = data.serviceName || data.title || event.title || '';
  const patientName = data.patientName || '';

  const timeRange = useMemo(() => {
    if (!event.start || !event.end) return '';
    return `${format(event.start, 'HH:mm')}–${format(event.end, 'HH:mm')}`;
  }, [event.start, event.end]);

  const durationMin = useMemo(() => {
    if (!event.start || !event.end) return null;
    return Math.round((event.end - event.start) / 60000);
  }, [event.start, event.end]);

  // All-day / cohort events
  if (event.allDay || data.type === 'cohort') {
    return (
      <div className="flex items-center gap-1.5 px-1 overflow-hidden">
        <span
          className="w-2 h-2 rounded-full shrink-0"
          style={{ backgroundColor: site.dot }}
        />
        <span className="text-[11px] font-semibold truncate">
          {data.title || event.title}
        </span>
        {data.metadata?.bookedSeats != null && (
          <span className="text-[9px] text-stone-400 shrink-0">
            {data.metadata.bookedSeats}/{data.metadata.totalSeats}
          </span>
        )}
      </div>
    );
  }

  // Timed booking events
  return (
    <TooltipProvider delayDuration={300}>
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            className="flex flex-col h-full overflow-hidden relative"
            style={{ borderLeft: `4px solid ${site.dot}`, borderRadius: '6px', marginLeft: '-2px' }}
          >
            <div className="flex items-start justify-between gap-1 px-1.5 pt-0.5 min-w-0">
              <span className="text-[11px] font-semibold text-stone-900 truncate leading-tight">
                {serviceName}
              </span>
              <span className={cn('w-2 h-2 rounded-full shrink-0 mt-0.5', statusClass)} />
            </div>
            {patientName && (
              <span className="text-[10px] text-stone-500 truncate px-1.5 leading-tight">
                {patientName}
              </span>
            )}
            {durationMin && durationMin >= 30 && (
              <div className="flex items-center justify-between px-1.5 mt-auto pb-0.5">
                <span className="text-[9px] text-stone-400">{timeRange}</span>
                <span className="text-[9px] text-stone-400 font-medium">{durationMin}m</span>
              </div>
            )}
          </div>
        </TooltipTrigger>
        <TooltipContent side="right" className="max-w-xs">
          <div className="space-y-1.5">
            <p className="font-semibold text-sm">{serviceName}</p>
            {patientName && <p className="text-xs text-muted-foreground">Patient: {patientName}</p>}
            {data.providerName && <p className="text-xs text-muted-foreground">Provider: {data.providerName}</p>}
            <p className="text-xs text-muted-foreground">Time: {timeRange}</p>
            {durationMin && <p className="text-xs text-muted-foreground">Duration: {durationMin} min</p>}
            <p className="text-xs text-muted-foreground capitalize">Status: {data.status}</p>
            <p className="text-xs">
              <span className="inline-flex items-center gap-1">
                <span className="w-2 h-2 rounded-full" style={{ backgroundColor: site.dot }} />
                {SITES[data.siteKey]?.label || data.siteKey}
              </span>
            </p>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

// Month view event — compact dot + title
export function CalendarMonthEvent({ event }) {
  const data = event.data || {};
  const site = SITES[data.siteKey] || { dot: '#a8a29e' };

  return (
    <div className="flex items-center gap-1.5 overflow-hidden">
      <span
        className="w-2 h-2 rounded-full shrink-0"
        style={{ backgroundColor: site.dot }}
      />
      <span className="text-[11px] truncate">{data.title || event.title}</span>
    </div>
  );
}
